import { inject, Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  collection,
  collectionData,
  doc,
  docData,
  getDocs,
  Firestore,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { MODEL_REQUESTS } from '@aveespa/common/constants/models-metadata.constants';
import { RequestStatus } from '@aveespa/common/enums/requests/request.enum';
import { StepKind, StepStatus } from '@aveespa/common/enums/requests/request-step.enum';
import type { RequestStep } from '@aveespa/common/models/requests/request-step.interface';
import type { Request as AppRequest } from '@aveespa/common/models/requests/request.interface';
import { RequestSummary } from '@aveespa/common/models/requests/request-summary.interface';
import { TenantContextService } from 'app/@core/services/utility/tenant-context.service';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RequestsService {
  private readonly _firestore  = inject(Firestore);
  private readonly _auth       = inject(Auth);
  private readonly _tenantCtx  = inject(TenantContextService);

  // CF callables — use inject(Functions) directly, matching the established pattern
  private readonly _cancelRequestFn = httpsCallable<
    { requestId: string; reason: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-cancelRequest');

  private readonly _deleteDraftRequestFn = httpsCallable<
    { requestId: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-deleteDraftRequest');

  private readonly _rejectRequestFn = httpsCallable<
    { requestId: string; reason: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-rejectRequest');

  private readonly _duplicateRequestFn = httpsCallable<
    { requestId: string }, { requestId: string }
  >(inject(Functions), 'requestLifecycleFunctions-duplicateRequest');

  private readonly _getPdfDownloadUrlFn = httpsCallable<
    { requestId: string }, { url: string }
  >(inject(Functions), 'requestLifecycleFunctions-getPdfDownloadUrl');

  private readonly _skipStepFn = httpsCallable<
    { requestId: string; stepId: string; reason: string }, { ok: boolean; completed: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-skipStep');

  private readonly _reassignStepFn = httpsCallable<
    { requestId: string; stepId: string; newAssigneeUid: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-reassignStep');

  private readonly _resendExternalInvitationFn = httpsCallable<
    { requestId: string; stepId: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-resendExternalInvitation');

  private readonly _resendCompletedDocumentFn = httpsCallable<
    { requestId: string; stepId: string }, { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-resendCompletedDocument');

  private readonly _createRequestFn = httpsCallable<
    { templateId: string; subject: string; summary?: string; selectedLanguage?: string; clientName?: string },
    { requestId: string }
  >(inject(Functions), 'requestLifecycleFunctions-createRequest');

  private readonly _sendRequestFn = httpsCallable<
    {
      requestId: string;
      externalParticipants?: Array<{
        stepId: string;
        name:   string;
        email:  string;
        phone?: string;
      }>;
      payPeriodKey?: string;
    },
    { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-sendRequest');

  private readonly _submitStepFn = httpsCallable<
    { requestId: string; stepId: string; values: Record<string, any>; lockVersion?: number },
    { ok: boolean; completed: boolean; rejected?: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-submitStep');

  private readonly _saveDraftFieldValuesFn = httpsCallable<
    { requestId: string; values: Record<string, any> },
    { ok: boolean }
  >(inject(Functions), 'requestLifecycleFunctions-saveDraftFieldValues');

  // ── Accessors ────────────────────────────────────────────────────────────────

  private get _tenantId(): string { return this._tenantCtx.currentTenantId!; }
  private get _uid(): string      { return this._auth.currentUser!.uid; }

  // ── Queries ──────────────────────────────────────────────────────────────────

  /**
   * Live observable of requests created by the current user, ordered by last update.
   * Requires a composite Firestore index on [tenantId ASC, createdBy.uid ASC, updatedAt DESC].
   */
  getMyRequestsForCurrentUser(): Observable<RequestSummary[]> {
    const q = query(
      collection(this._firestore, MODEL_REQUESTS),
      where('tenantId',        '==', this._tenantId),
      where('createdBy.uid',   '==', this._uid),
      orderBy('updatedAt',     'desc'),
    );
    return collectionData(q, { idField: 'id' }).pipe(
      map((docs) => (docs as (AppRequest & { id: string })[])
        .filter((d) => d.status !== RequestStatus.Deleted)
        .map((d) => this._toSummarySafe(d))
        .filter((s): s is RequestSummary => s !== null)),
    );
  }

  /**
   * Paginated one-time fetch ordered by updatedAt DESC.
   * Returns `lastDoc` for use as a startAfter cursor on the next call.
   * `lastDoc === null` means no further pages exist.
   * Requires the same composite index as getMyRequestsForCurrentUser.
   */
  async fetchMyRequestsPage(
    pageSize = 25,
    afterDoc?: QueryDocumentSnapshot,
  ): Promise<{ items: RequestSummary[]; lastDoc: QueryDocumentSnapshot | null }> {
    const snap = await getDocs(query(
      collection(this._firestore, MODEL_REQUESTS),
      where('tenantId',      '==', this._tenantId),
      where('createdBy.uid', '==', this._uid),
      orderBy('updatedAt',   'desc'),
      limit(pageSize),
      ...(afterDoc ? [startAfter(afterDoc)] : []),
    ));
    const items   = snap.docs
      .filter(d => (d.data() as AppRequest).status !== RequestStatus.Deleted)
      .map(d => this._toSummarySafe({ ...d.data() as AppRequest, id: d.id }))
      .filter((s): s is RequestSummary => s !== null);
    const lastDoc = snap.docs.length === pageSize
      ? snap.docs[snap.docs.length - 1] as QueryDocumentSnapshot
      : null;
    return { items, lastDoc };
  }

  /**
   * Live observable of a single request document.
   * Used by the detail view — emits null when the document doesn't exist.
   */
  getRequestById(requestId: string): Observable<AppRequest | null> {
    const ref = doc(collection(this._firestore, MODEL_REQUESTS), requestId);
    return (docData(ref, { idField: 'id' }) as Observable<AppRequest & { id: string } | undefined>).pipe(
      map((d) => d ?? null),
    );
  }

  // ── Write helpers ─────────────────────────────────────────────────────────────

  /** Archives or unarchives a request (pure UI visibility flag — no lifecycle impact). */
  archiveRequest(requestId: string, archived: boolean): Promise<void> {
    const ref = doc(collection(this._firestore, MODEL_REQUESTS), requestId);
    return updateDoc(ref, { archived });
  }

  cancelRequest(requestId: string, reason: string): Promise<void> {
    return this._cancelRequestFn({ requestId, reason }).then(() => undefined);
  }

  /** Logical delete for a Draft nobody else has ever seen — see RequestStatus.Deleted (M4-24). */
  deleteDraftRequest(requestId: string): Promise<void> {
    return this._deleteDraftRequestFn({ requestId }).then(() => undefined);
  }

  rejectRequest(requestId: string, reason: string): Promise<void> {
    return this._rejectRequestFn({ requestId, reason }).then(() => undefined);
  }

  /** Returns the new requestId of the duplicate draft. */
  duplicateRequest(requestId: string): Promise<string> {
    return this._duplicateRequestFn({ requestId }).then((r) => r.data.requestId);
  }

  /** Returns a 60-second signed Storage URL for the completed PDF. */
  getPdfDownloadUrl(requestId: string): Promise<string> {
    return this._getPdfDownloadUrlFn({ requestId }).then((r) => r.data.url);
  }

  /** Skips an optional active step and advances the workflow. */
  skipStep(requestId: string, stepId: string, reason: string): Promise<{ completed: boolean }> {
    return this._skipStepFn({ requestId, stepId, reason }).then((r) => ({ completed: r.data.completed }));
  }

  /** Reassigns an active step to a different staff member. */
  reassignStep(requestId: string, stepId: string, newAssigneeUid: string): Promise<void> {
    return this._reassignStepFn({ requestId, stepId, newAssigneeUid }).then(() => undefined);
  }

  /** Re-dispatches the sign-invitation email for an Active ExternalParticipant step — rotates the token, so the old link stops working. */
  resendExternalInvitation(requestId: string, stepId: string): Promise<void> {
    return this._resendExternalInvitationFn({ requestId, stepId }).then(() => undefined);
  }

  /** Re-sends the completed-document email for a Completed ExternalParticipant step — for a lost email or a blocked attachment. */
  resendCompletedDocument(requestId: string, stepId: string): Promise<void> {
    return this._resendCompletedDocumentFn({ requestId, stepId }).then(() => undefined);
  }

  /** Creates a new Draft request from a published template. Returns the new requestId. */
  createRequest(templateId: string, subject: string, selectedLanguage?: string, clientName?: string): Promise<string> {
    return this._createRequestFn({ templateId, subject, selectedLanguage, clientName }).then(r => r.data.requestId);
  }

  /**
   * Transitions a Draft request to InProgress, activating step 0 and dispatching
   * invitation emails. payPeriodKey is required when the template supports pay
   * periods and "now" falls in a grace window (M4-PP-01) — the server validates
   * it independently and rejects a stale/mismatched key.
   */
  sendRequest(
    requestId: string,
    externalParticipants?: Array<{
      stepId: string;
      name:   string;
      email:  string;
      phone?: string;
    }>,
    payPeriodKey?: string,
  ): Promise<void> {
    return this._sendRequestFn({ requestId, externalParticipants, payPeriodKey }).then(() => undefined);
  }

  /** Submits a step's data payload, advancing the request to the next step (or completing it). */
  submitStep(requestId: string, stepId: string, values: Record<string, any>): Promise<{ completed: boolean }> {
    return this._submitStepFn({ requestId, stepId, values }).then(r => ({ completed: r.data.completed }));
  }

  /**
   * Persists the requester's in-progress field values on a Draft (debounced autosave
   * from the request editor). No-op server-side once the request has left Draft.
   */
  saveDraftFieldValues(requestId: string, values: Record<string, any>): Promise<void> {
    return this._saveDraftFieldValuesFn({ requestId, values }).then(() => undefined);
  }

  // ── Mapping ───────────────────────────────────────────────────────────────────

  /** Wraps _toSummary so one malformed document can't blank the entire list — skips and logs instead. */
  private _toSummarySafe(req: AppRequest & { id: string }): RequestSummary | null {
    try {
      return this._toSummary(req);
    } catch (err) {
      console.error(`[RequestsService] Failed to summarize request ${req.id} — skipping.`, err);
      return null;
    }
  }

  private _toSummary(req: AppRequest & { id: string }): RequestSummary {
    // Array.isArray guard (not just ?? []) — a request whose `steps` field was ever
    // corrupted into a map by the CF dot-notation bug (now fixed) would otherwise
    // throw here and, uncaught, blank the entire My Requests list for one bad doc.
    const steps = Array.isArray(req.steps) ? req.steps : [];
    const externalStep   = steps.find((s) => s.kind === StepKind.ExternalParticipant);
    const completedSteps = steps.filter(
      (s) => s.status === StepStatus.Completed || s.status === StepStatus.Skipped,
    ).length;

    const activeStep = steps.find((s: RequestStep) => s.status === StepStatus.Active);
    const activeStepAssigneeUid = activeStep?.kind === StepKind.InternalUser
      ? (activeStep.assignedTo?.uid ?? undefined)
      : undefined;
    const activeStepKind = activeStep?.kind;

    // Template-driven showInOverview fields (M4-25), denormalized server-side —
    // see saveDraftFieldValues / submitStep. Falls back to whatever the request
    // itself carries when the template defines no overview fields.
    const displayFields: Record<string, string> = { ...req.displayFields };
    if (!Object.keys(displayFields).length && req.searchableExternalUser?.displayName) {
      displayFields['clientName'] = req.searchableExternalUser.displayName;
    }
    if (req.rejectionReason) {
      displayFields['rejectionReason'] = req.rejectionReason;
    }

    return {
      id:                   req.id,
      confirmationCode:     req.confirmationCode,
      templateId:           req.templateId,
      templateVersion:      req.templateVersion,
      templateName:         req.templateName ?? req.templateId,
      subject:              req.subject?.trim() || req.templateName || req.templateId,
      departmentId:         req.departmentId,
      status:               req.status,
      isDraft:              req.status === RequestStatus.Draft,
      archived:             req.archived ?? false,
      createdAt:            req.createdAt,
      lastUpdatedAt:        req.updatedAt,
      totalSteps:           steps.length,
      completedSteps,
      currentStepIndex:     req.currentStepIndex,
      primaryRecipientName:  externalStep?.external?.name,
      primaryRecipientEmail: externalStep?.external?.email,
      displayFields,
      payPeriod:            req.payPeriod,
      activeStepAssigneeUid,
      activeStepKind,
    };
  }
}
